import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Truck, Package, ExternalLink } from "lucide-react";
import toast from "react-hot-toast";

const ShippingModal = ({ isOpen, onClose, onSubmit, order, isLoading }) => {
  const [formData, setFormData] = useState({
    shipping_company: "",
    tracking_number: "",
    tracking_url: "",
  });

  const [errors, setErrors] = useState({});

  // Popular shipping companies for quick selection
  const popularCompanies = [
    "Blue Dart",
    "DTDC",
    "FedEx",
    "DHL",
    "India Post",
    "Aramex",
    "Ecom Express",
    "Delhivery",
    "Other",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCompanySelect = (company) => {
    setFormData((prev) => ({
      ...prev,
      shipping_company: company,
    }));
    setErrors((prev) => ({
      ...prev,
      shipping_company: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.shipping_company.trim()) {
      newErrors.shipping_company = "Shipping company is required";
    }

    if (!formData.tracking_number.trim()) {
      newErrors.tracking_number = "Tracking number is required";
    }

    // Optional URL validation
    if (formData.tracking_url && formData.tracking_url.trim()) {
      const urlPattern =
        /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlPattern.test(formData.tracking_url.trim())) {
        newErrors.tracking_url = "Please enter a valid URL";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form on success
      setFormData({
        shipping_company: "",
        tracking_number: "",
        tracking_url: "",
      });
      setErrors({});
    } catch (error) {
      console.error("Shipping form error:", error);
    }
  };

  const handleClose = () => {
    setFormData({
      shipping_company: "",
      tracking_number: "",
      tracking_url: "",
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={handleClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Shipping Information
                </h3>
                <p className="text-sm text-gray-600">
                  Order #{order?.order_number}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isLoading}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Shipping Company */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Shipping Company *
              </label>

              {/* Quick Select Buttons */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {popularCompanies.map((company) => (
                  <button
                    key={company}
                    type="button"
                    onClick={() => handleCompanySelect(company)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      formData.shipping_company === company
                        ? "bg-blue-50 border-blue-200 text-blue-700"
                        : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {company}
                  </button>
                ))}
              </div>

              {/* Manual Input */}
              <input
                type="text"
                name="shipping_company"
                value={formData.shipping_company}
                onChange={handleInputChange}
                placeholder="Enter shipping company name"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.shipping_company ? "border-red-300" : "border-gray-300"
                }`}
                disabled={isLoading}
              />
              {errors.shipping_company && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.shipping_company}
                </p>
              )}
            </div>

            {/* Tracking Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tracking Number *
              </label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  name="tracking_number"
                  value={formData.tracking_number}
                  onChange={handleInputChange}
                  placeholder="Enter tracking/AWB number"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.tracking_number
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  disabled={isLoading}
                />
              </div>
              {errors.tracking_number && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.tracking_number}
                </p>
              )}
            </div>

            {/* Tracking URL (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tracking URL (Optional)
              </label>
              <div className="relative">
                <ExternalLink className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="url"
                  name="tracking_url"
                  value={formData.tracking_url}
                  onChange={handleInputChange}
                  placeholder="https://track.company.com/tracking-id"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.tracking_url ? "border-red-300" : "border-gray-300"
                  }`}
                  disabled={isLoading}
                />
              </div>
              {errors.tracking_url && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.tracking_url}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Add a direct link to track the package (optional)
              </p>
            </div>

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Mark as Shipped"
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ShippingModal;
